/* global process */
/**
 * @module lib/Api
 * Provides utility functions for making authenticated api requests to Pandoras
 * backend services
 */
import axios from 'axios';
import axiosRetry from 'axios-retry';
import Cookies from 'js-cookie';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isError from 'lodash/isError';
import isObject from 'lodash/isObject';
import { normalize } from 'normalizr';

import { sendServiceErrorToMiddleware } from '../../actions/Api';
import {
  serviceError,
  serviceOk,
  switchAppOffline,
  switchAppOnline,
} from '../../actions/Application';
import * as Constants from '../../constants';
import {
  API_PATH,
  ApiEndpoints,
  ApiResponseTypes,
  ServiceHostTypes,
} from '../../constants/ApiActions';
import {
  getPaidHostApiPath,
  getSheerIdClientApiBaseUrl,
  getStatsJsonApiUrl,
} from '../../lib/configuration/TunerVarUtils';
import ApiError from '../../lib/error/ApiError';
import { getAuthToken } from '../../lib/ListenerUtils';
import { Schemas } from '../../store/Schemas';
import { getStore } from '../../store/StoreAccessor';
import Logger from '../../tools/Logger';
import * as Utils from '../../utils';
import { ERROR_TYPE_API } from '../DatadogClient';
import { getUrlQueryParameters } from '../utils/url';

const log = Logger.child({
  component: 'lib/Api',
  errorType: ERROR_TYPE_API,
});

let fetch;
let ApiModule;

function getFullEndpoint(serviceHostType, endpoint, queryString) {
  const { getState } = getStore();
  const hostMap = {
    [ServiceHostTypes.DEFAULT]: `${API_PATH}/${endpoint}`,
    [ServiceHostTypes.TWITTER]: queryString ? `/${endpoint}${queryString}` : `/${endpoint}`,
    [ServiceHostTypes.SEARCH_AUTOCOMPLETE]: `/${endpoint}`,
    [ServiceHostTypes.PAID]: getPaidHostApiPath(getState(), endpoint),
    [ServiceHostTypes.COMMUNITY_SSO]: Utils.Application.getCommunitySSOUrl(),
    [ServiceHostTypes.STATS]: getStatsJsonApiUrl(getState()),
    [ServiceHostTypes.SHEER_ID]: `${getSheerIdClientApiBaseUrl(getState())}/${endpoint}`,
  };
  return hostMap[serviceHostType];
}

/**
 * Gets or creates an instance of Axios
 *
 * @param  {string} serviceHost the root domain of the host
 * @returns {object} Axios instance
 */
function getAPIInstance() {
  fetch = fetch || axios.create();
  return fetch;
}

/**
 * Populate the supplied header information with applicable content-type and
 * AuthToken
 *
 * @param  {string} responseType - responseType of request to configure
 * @param  {object} headers - the current headers of the request
 * @param  {object} content - the current content of the request
 * @param  {string} authToken - the supplied authToken if applicable
 */
export function configureHeaders(responseType, headers, content, authToken) {
  const { getState } = getStore();

  const xAuthToken = authToken || getAuthToken(getState());
  if (xAuthToken) {
    headers['X-AuthToken'] = xAuthToken;
  }

  // passes header to trigger non-standard login attempt from human security
  if (!('captcha' in content) && getUrlQueryParameters().forceCaptcha) {
    headers['x-minos-qa'] = 'slider';
  }

  const cookieValue = Cookies.get('csrftoken');
  if (cookieValue) {
    headers['X-CsrfToken'] = cookieValue;
  }

  if (responseType !== ApiResponseTypes.PLAINTEXT && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
}

/**
 * Intercept Axios response and dispatch appropriate actions upon success
 *
 * @param {object} action - redux action
 * @returns {Promise}
 */
const defaultSuccessInterceptor = action => response => {
  const { dispatch, getState } = getStore();
  if (Utils.Application.isApplicationOffline(getState())) {
    dispatch(switchAppOnline());
  }
  if (response.status === 200) {
    dispatch(serviceOk());
    return response;
  }
  dispatch(serviceError(response, action));
  return Promise.reject(response);
};

/**
 * Intercepts successful api service response. Some services like graphql may contain errors within
 * its success response so additional checks are needed before dispatching the proper api action type.
 *
 * @param {object} successResponse  - api response
 * @param {object} requestParams - original request object
 * @returns {boolean} true/false
 */
export function detectInternalServiceError(successResponse, requestParams) {
  const isGraphQLApiCall = get(requestParams, 'endpoint', '') === ApiEndpoints.GRAPHQL;

  if (isGraphQLApiCall) {
    return !!get(successResponse, ['result', 'errors'], null);
  }

  return false;
}

/**
 * Intercept Axios response and dispatch appropriate actions upon error
 *
 * @param {object} action - redux action
 * @param {string} serviceHostType - the host for the intercepted request
 * @returns {Promise}
 */
const defaultErrorInterceptor = (action, serviceHostType) => error => {
  const { dispatch, getState } = getStore();
  // The main case where we won't have a response is when the request times out
  const modifiedError = error && error.response ? error.response : error;
  dispatch(serviceError(modifiedError, action));
  if (
    error &&
    error.message === Constants.ServiceErrors.NETWORK_ERROR &&
    serviceHostType !== ServiceHostTypes.STATS
  ) {
    if (!Utils.Application.isApplicationOffline(getState())) {
      dispatch(switchAppOffline());
    }
  }
  return Promise.reject(modifiedError);
};

/**
 * Set interceptors for axios, so that we can do some preprocessing of responses
 *
 * @param {object} config - interceptor configuration
 * @param config.successInterceptor
 * @param config.errorInterceptor
 * @returns {object} - configured response interceptors
 */
function setResponseInterceptor({ successInterceptor, errorInterceptor }) {
  const apiInstance = ApiModule.getAPIInstance();
  // remove existing interceptors so they do not get called for future requests
  apiInstance.interceptors.response.handlers.forEach((handler, index) => {
    if (handler) {
      apiInstance.interceptors.response.eject(index);
    }
  });

  // set interceptors
  apiInstance.interceptors.response.use(successInterceptor, errorInterceptor);

  axiosRetry(apiInstance, { retries: 0 });

  // return the interceptors so that they can be ejected
  return { successInterceptor, errorInterceptor };
}

function setRequestInterceptor(requestInterceptor) {
  const apiInstance = ApiModule.getAPIInstance();
  apiInstance.interceptors.request.use(requestInterceptor);
}

/**
 * Makes a very dumbed down synchronous XMLHttpRequest used for pinging pausePlayback before shutdown
 *
 * @param {string} endpoint - the request to make
 */
function makeSynchronousRequest({ endpoint, method = 'post', headers = {}, content }) {
  // No need to worry about ActiveX since no browsers from that era should get here
  const xhr = new XMLHttpRequest();
  xhr.open(method, endpoint, false);
  for (const header of Object.keys(headers)) {
    xhr.setRequestHeader(header, headers[header]);
  }
  xhr.send(JSON.stringify(content));
}

/**
 * Make ASYNC request with Axios.
 *
 * @param root0
 * @param root0.endpoint
 * @param root0.method
 * @param root0.content
 * @param root0.params
 * @param root0.schema
 * @param root0.retries
 * @param root0.timeout
 * @param root0.parser
 * @param root0.headers
 * @param root0.responseType
 * @param root0.action
 * @param root0.signal
 * @param root0.serviceHostType
 * @param root0.withCredentials
 * @param root0.retryCondition
 * @returns {Promise}
 */
function makeAsynchronousRequest({
  endpoint,
  method = 'post',
  content = {},
  params = {},
  schema,
  retries = 0,
  timeout = 0,
  parser = null,
  headers = {},
  responseType,
  action,
  signal,
  serviceHostType,
  withCredentials = true,
  retryCondition = null,
}) {
  const apiInstance = getAPIInstance();

  ApiModule.setResponseInterceptor({
    successInterceptor: ApiModule.defaultSuccessInterceptor(action),
    errorInterceptor: ApiModule.defaultErrorInterceptor(action, serviceHostType),
  });

  const axiosRetryConfig = {
    retries,
  };

  if (retryCondition) {
    axiosRetryConfig.retryCondition = retryCondition;
  }

  return apiInstance
    .request({
      url: endpoint,
      // axios automatically adds a 'Content-Type: application/json' header to the request
      // when 'data' is an object, even if it's empty, and this messes up autocomplete api
      // responses (they show up as an empty line). We need the Content-Type header to not
      // be set at all, so We're using the optional responseType param to determine if we
      // should pass a data object at all.
      data: responseType === ApiResponseTypes.PLAINTEXT ? null : content,
      params,
      method,
      signal,
      'axios-retry': axiosRetryConfig,
      headers,
      withCredentials,
      timeout,
    })
    .then(response => {
      let { data } = response;
      if (response.status !== 200) {
        return Promise.reject(response);
      }
      if (typeof parser === 'function') {
        data = parser(data);
      }
      if (schema === Schemas.NO_ENTITY) {
        return data;
      }
      if (!isObject(data)) {
        log.error(`Unexpected successful return without data object for ${endpoint}`);
        return data;
      }
      return normalize(data, schema);
    })
    .catch(error => {
      return Promise.reject(error);
    });
}

/**
 * Make asynchronous request as page is unloading using navigator.sendBeacon().
 *
 * @param root0
 * @param root0.endpoint
 * @param root0.content
 */
function makeBeaconRequest({ endpoint, content = {} }) {
  const result = navigator.sendBeacon(endpoint, content);
  if (!result) {
    log.error(`sendBeacon reported failed post for ${endpoint}`);
  }
}

/**
 * Attempts configured API request and handles returned response by normalizing
 * supplied Normalizr Schema
 *
 * @param root0
 * @param root0.endpoint
 * @param root0.method
 * @param root0.serviceHostType
 * @param root0.content
 * @param root0.params
 * @param root0.schema
 * @param root0.sync
 * @param root0.retries
 * @param root0.timeout
 * @param root0.parser
 * @param root0.authToken
 * @param root0.excludeAuthTokenInHeader
 * @param root0.excludeCsrfTokenInHeader
 * @param root0.withCredentials
 * @param root0.headers
 * @param root0.responseType
 * @param root0.action
 * @param root0.queryString
 * @param root0.signal
 * @param root0.retryCondition
 * @returns {Promise}
 */
function makeApiRequest({
  endpoint,
  method = 'post',
  serviceHostType = ServiceHostTypes.DEFAULT,
  content = {},
  params = {},
  schema,
  sync = false,
  retries = 0,
  timeout = 0,
  parser = null,
  authToken = null,
  excludeAuthTokenInHeader = false,
  excludeCsrfTokenInHeader = false,
  withCredentials = true,
  headers = {},
  responseType,
  action,
  queryString,
  signal,
  retryCondition = null,
}) {
  // adds appropriate headers
  ApiModule.configureHeaders(responseType, headers, content, authToken);

  if (excludeAuthTokenInHeader) {
    delete headers['X-AuthToken'];
  }
  if (excludeCsrfTokenInHeader) {
    delete headers['X-CsrfToken'];
  }

  // determine serviceHost
  const fullEndpoint = getFullEndpoint(serviceHostType, endpoint, queryString);

  if (responseType === ApiResponseTypes.PLAINTEXT && !isEmpty(content)) {
    log.error(
      "responseType is 'plaintext' but content is non-empty, this will result in content not being passed to api request method due to issues with axios setting Content-Type header.",
    );
  }

  // SYNC request
  if (sync) {
    if (navigator && typeof navigator.sendBeacon === 'function') {
      return Promise.resolve(ApiModule.makeBeaconRequest({ endpoint: fullEndpoint, content }));
    }
    return Promise.resolve(
      ApiModule.makeSynchronousRequest({
        endpoint: fullEndpoint,
        method,
        headers,
        content,
      }),
    );
  }

  // ASYNC request
  return ApiModule.makeAsynchronousRequest({
    endpoint: fullEndpoint,
    method,
    content,
    params,
    schema,
    parser,
    authToken,
    retries,
    timeout,
    headers,
    responseType,
    action,
    serviceHostType,
    signal,
    withCredentials,
    retryCondition,
  });
}

/**
 * Make an API request to Pandora service dispatching actions for attempt, success
 * and complete states.
 *
 * @param  {object} requestParams - request params
 * @returns {Promise} - API response
 */
function apiRequest(requestParams) {
  const { dispatch } = getStore();
  const [requestType, successType, failureType] = Utils.Api.getApiActionTypes(requestParams);
  const schema = Utils.Api.getSchema(requestParams);
  const params = Object.assign({}, requestParams, {
    schema,
  });

  // dispatch attempt action
  dispatch({
    type: requestType,
    // to keep the equiv behavior as the middleware was producing
    request: {
      content: requestParams.content || {},
      query: requestParams.query || null,
      params: requestParams.params || {},
    },
  });

  // delegate actual API request and return response
  return new Promise((resolve, reject) => {
    return ApiModule.makeApiRequest(params)
      .then(response => {
        const actionType = detectInternalServiceError(response, requestParams)
          ? failureType
          : successType;

        if (actionType === failureType) {
          log.warn(`Success response contains internal service error: ${actionType}`);
        }

        // dispatch success action unless internal service error detected
        dispatch({
          type: actionType,
          response,
          params: requestParams,
          ...params.actionParams,
        });
        // return response to caller
        return resolve(response);
      })
      .catch(errorResponse => {
        let error = null;
        if (isError(errorResponse)) {
          error = errorResponse;
        } else {
          // Most common case. If no timeout or application error construct an ApiError.
          error = new ApiError(errorResponse, params);
        }

        // dispatch error action
        dispatch({
          type: failureType,
          error,
          ...params.actionParams,
        });

        // if this is an error we want to send it over to the middleware to process for service errors
        dispatch(sendServiceErrorToMiddleware(params, error))
          .then(response => {
            // We recovered from a service error
            return resolve(response);
          })
          .catch(() => {
            return reject(error);
          });
      });
  });
}

/**
 * Delete any null parameters from given parameter object.
 * ..for server APIs that can't handle the default blank strings as nulls.
 * This explicitly deletes parameters passed as javascript null, which axios normally turns into blank strings.
 * All other falsy values ('', [], {}, false, 0, etc) are passed through unchanged.
 *
 * @param  {object} params - request params.
 * @returns {object} - params with all null params deleted.
 */

function deleteNullParams(params) {
  const resultParams = { ...params };
  for (const param in resultParams) {
    if (resultParams[param] === null) {
      delete resultParams[param];
    }
  }
  return resultParams;
}

/**
 * Export Module
 */
export default ApiModule = {
  getAPIInstance,
  configureHeaders,
  defaultSuccessInterceptor,
  defaultErrorInterceptor,
  setResponseInterceptor,
  setRequestInterceptor,
  makeSynchronousRequest,
  makeAsynchronousRequest,
  makeBeaconRequest,
  makeApiRequest,
  apiRequest,
  deleteNullParams,
};
